import { useState, useEffect, useMemo } from 'react';
import { bannerAdPositions } from '@Components/Utilities/SandhillsConstants';
import { sandhillsRequest, getBannerAd, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, setScrollTop } from '@Components/Utilities/SandhillsUtility';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import Paging from '@Components/MuiWrappers/Paging';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import TranslationText from '@Components/Utilities/TranslationText';
import JobListing from '@Components/JobListings/JobListing';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import ready from 'document-ready';
import debounce from 'lodash.debounce';

const JobListingsView = (props) => {

    const topBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Top);
    const [searchData, setSearchData] = useState({
        SearchBaseURL: props.AjaxSearchURL,
        SearchPageURL: props.SearchPageURL,
        JobListings: props.JobListings,
        SearchFields: props.SearchFields,
        ListingsCount: props.MatchCount,
        PageHeader: props.PageHeader,
        PageTitle: props.PageTitle,
        CurrentPage: props.CurrentPage ? props.CurrentPage : 1,
        TotalPages: props.PageCount,
        IsDefaultSearch: props.IsDefaultSearch,
        CategoryInfo: props.CategoryInformation
    });
    const currentSite = useRecoilValue(atoms.currentWebsite);

    const [isMounted, setIsMounted] = useState(false);
    const [showSearchWindow, setShowSearchWindow] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);

    useEffect(function runPageSearch() {
        if (isMounted) {
            runSearch(true);
        }
    }, [searchData.CurrentPage]);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
            setIsMounted(true);
        });

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    const buildUrl = (keepPage, pendingChanges) => {
        let searchUrl = searchData.SearchBaseURL;
        if (pendingChanges) {
            pendingChanges.forEach(field => {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);
                if (field.value) {
                    searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                }
            });
        }

        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, 'Page', searchData.CurrentPage);
        }

        return searchUrl;
    };

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName == pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName == field.FieldName && field.Type == 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value:
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };

    const runSearch = (keepPage, latestFieldValue) => {
        let pendingChanges = [];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        if (window.innerWidth <= 785) {
            setScrollTop(0);
        }

        performSearch(buildUrl(keepPage, pendingChanges));
    };

    const performSearch = (newSearchURL) => {
        setSearchInProgress(true);
        sandhillsRequest({
            url: newSearchURL,
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            handleResponse(data);
        });
    };

    const handleResponse = (result) => {
        setSearchData({
            SearchBaseURL: result.AjaxSearchURL,
            SearchPageURL: result.SearchPageURL,
            JobListings: result.JobListings,
            SearchFields: result.SearchFields,
            ListingsCount: result.MatchCount,
            PageHeader: result.PageHeader,
            PageTitle: result.PageTitle,
            CurrentPage: result.CurrentPage ? result.CurrentPage : 1,
            TotalPages: result.PageCount,
            IsDefaultSearch: result.IsDefaultSearch,
            CategoryInfo: result.CategoryInformation
        });
        setSearchInProgress(false);
        window.history.pushState('', result.MetaTitle, result.SearchPageURL);
    };

    const handlePageNumberChange = (page) => {
        setSearchData(previousState => {
            return { ...previousState, CurrentPage: page };
        });
        setScrollTop(0);
    };

    const toggleSearchWindow = () => {
        let showWindow = !showSearchWindow;
        if (showWindow) {
            document.getElementsByTagName('body')?.[0]?.classList.add('fixed');
        } else {
            document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
        }

        setShowSearchWindow(showWindow);
        setMobileSearch(showWindow && window.innerWidth <= 785)
    };

    const clearAll = () => {
        setScrollTop(0);
        setSearchInProgress(true);
        sandhillsRequest({
            url: props.ClearAllURL,
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            handleResponse(data);
        });
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false);
        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = searchData.SearchFields.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && selectedField.ParentNameForSearch) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0) {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        performSearch(searchUrl);
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    return (
        <div className='job-listings-wrapper'>
            <CategoryInformation {...searchData.CategoryInfo} />
            <section className='list-top-section'>
                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={searchData.PageHeader} />
                    <h1 className='page-title'>
                        <span><strong>{searchData.PageHeader}</strong></span>
                        <span className='match-count'>&nbsp;<TranslationText tKey='sandhills.tradepubwebsite.mvc.numbermatches' />: {searchData.ListingsCount}</span>
                    </h1>
                {topBannerAd &&
                    <DoubleClickAd
                        {...props.DoubleClickAdCollection}
                        position={bannerAdPositions.Top}
                        ad={topBannerAd}
                        contained
                    />}
            </section>
            <section className='job-listings-bottom'>
                <div className='job-listings-view'>
                    <div className='side-container'>
                        <FacetedSearch
                            {...searchData}
                            runSearch={runSearch}
                            clearAll={clearAll}
                            clearFacet={clearFacetDebounced}
                            disabled={searchInProgress}
                            showMobileFilters={showSearchWindow}
                            toggleMobileFilter={toggleSearchWindow}
                            mobileSearch={mobileSearch}
                        />
                    </div>
                    <div className='job-listings-content'>
                        {searchData.JobListings && searchData.JobListings.length > 0 ? searchData.JobListings.map(item =>
                            <JobListing
                                key={item.JobListingID}
                                UseNewLayoutForJobsandIndustries={props.UseNewLayoutForJobsandIndustries}
                                {...item}
                            />) :
                            <div className='no-job-listings-filter'>
                                <div>
                                    <TranslationText tKey='sandhills.sorryyouhavefilteredoutall' />
                                </div>
                                <div>
                                    <TranslationText tKey='sandhills.tradesites.mvc.adjustyourfiltersorperformanewsearchbyselecting' />
                                    {'\u00A0'}
                                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); clearAll(); } }} onClick={clearAll}>
                                        '<TranslationText tKey='sandhills.clearall' />'
                                                    </a>
                                    {'\u00A0'}
                                    <TranslationText tKey='sandhills.tradesites.mvc.toseemorelistings' />
                                </div>
                            </div>}
                        {searchData.TotalPages > 1 &&
                            <Paging
                                currentPage={searchData.CurrentPage}
                                totalPages={searchData.TotalPages}
                                onChange={handlePageNumberChange}
                            />}
                    </div>
                </div>
            </section>
            <FacetedSearchMobileMenu
                filter={{
                    render: true,
                    toggle: toggleSearchWindow
                }}
            />
            {searchInProgress && <div className='searching-background' />}
            {currentSite.IsMobileAppContext &&
                <div className='detail__mobile-padding-bottom' />
            }
        </div>
    );
};

export default JobListingsView;
global.JobListingsView = JobListingsView;